import React, { useState, useMemo } from "react";
import { Button, Col, DatePicker, Divider, Form, Input, Modal, notification, Row, Select, Space, Table, Upload, message } from "antd";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { expenseVoucherService } from "../../../../../services";
import { v4 as uuidv4 } from "uuid";
import { CiSquarePlus } from "react-icons/ci";
import dayjs from "dayjs";
import MaterialLikeSelect from "../../../../../components/MaterialLikeSelect";
import MaterialLikeInput from "../../../../../components/MaterialLikeInput";
import MaterialLikeDatePicker from "../../../../../components/MaterialLikeDatePicker";
import MaterialLikeTextArea from "../../../../../components/MaterialLikeTextArea ";

function PayablePayment({ isVisible, setIsModalOpen, paymentmodes, banks, companies, vendors, vouchers, setRefresh }) {

  const [form2] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [showBankField, setShowBankField] = useState(false);
  const [data2, setData2] = useState([
    {
      Id: 1,
      ExpenseAccount: null,
      Description: "",
      DueAmount: "",
      PayAmount: "",
      BalAmount: "",
    },
  ]);

  const totalPayingAmount = useMemo(() => {
    return data2.reduce(
      (total, record) => total + Number(record.PayAmount || 0),
      0
    );
  }, [data2]);


  const columns2 = [
    {
      title: "Expense Account",
      dataIndex: "ExpenseAccount",
      key: "ExpenseAccount",
      render: (text, record, index) => (
        <>
          <Select
            value={record.ExpenseAccount}
            onChange={(value) => {
              handleDueAmount(value, record.Id);
              handleChange(value, "ExpenseAccount", record.Id);
            }}
            style={{ width: "100%" }}
          >
            {vouchers
              .filter((voucher) => voucher.is_paid === 0)
              .map((voucher) => (
                <Select.Option key={voucher.id} value={voucher.id}>
                  {voucher.voucher_number} - {voucher.id}
                </Select.Option>
              ))}
          </Select>
        </>
      ),
    },
    {
      title: "Description",
      dataIndex: "Description",
      key: "Description",
      render: (text, record, index) => (
        <>
          <Input
            value={record.Description}
            onChange={(e) =>
              handleChange(e.target.value, "Description", record.Id)
            }
            style={{ width: "100%" }}
          />
        </>
      ),
    },
    {
      title: "Due Amount",
      dataIndex: "due_amount",
      key: "due_amount",
      render: (text, record, index) => (
        <>
          <Input value={record.DueAmount} style={{ width: "100%" }} readOnly />
        </>
      ),
    },
    {
      title: "Pay Amount",
      dataIndex: "PayAmount",
      key: "PayAmount",
      render: (text, record, index) => (
        <>
          <Input
            value={record.PayAmount}
            onChange={(e) => handleChange(e.target.value, "PayAmount", record.Id)}
            style={{ width: "100%" }}
          />
        </>
      ),
    },
    {
      title: "Bal. Amount",
      dataIndex: "Bal.Amount",
      key: "Bal.Amount",
      render: (text, record, index) => (
        <>
          <Input
            value={record.BalAmount}
            onChange={(e) => handleChange(e.target.value, "BalAmount", record.Id)}
            style={{ width: "100%" }}
          />
        </>
      ),
    },
    {
      title: "Delete",
      dataIndex: "Delete",
      key: "Delete",
      render: (text, record) => {
        if (data2.length === 1) return null;
        return (
          <Space size="small">
            <Button
              type="primary"
              size="small"
              onClick={() => handleDeleteRow(record)}
            >
              <DeleteOutlined />
            </Button>
          </Space>
        );
      },
    },
  ];


  const handleDueAmount = async (value, recordId) => {
    const selectedVoucher = vouchers.find(
      (voucher) => voucher.id === value
    );
    if (selectedVoucher) {
      setData2((prevData) =>
        prevData.map((item) =>
          item.Id == recordId
            ? {
              ...item,
              DueAmount: selectedVoucher.amount || 0,
              BalAmount: selectedVoucher.amount - item.PayAmount,
            }
            : item
        )
      );
    }
  };

  const handleChange = (value, field, recordId) => {
    setData2((prevData) =>
      prevData.map((record) => {
        if (record.Id === recordId) {
          const updatedRecord = { ...record, [field]: value };

          if (field === "PayAmount") {
            updatedRecord.BalAmount = (updatedRecord.DueAmount || 0) - value;
          }

          return updatedRecord;
        }
        return record;
      })
    );
  };

  const handleAddRow = () => {
    setData2([
      ...data2,
      {
        Id: uuidv4(),
        ExpenseAccount: "",
        Description: "",
        DueAmount: "",
        PayAmount: "",
        BalAmount: "",
      },
    ]);
  };

  const handleDeleteRow = (record) => {
    setData2(data2.filter((item) => item.Id !== record.Id));
  };


  const handleCancel = (close = false) => {

    if (!close) setRefresh(true);

    setData2([{
      Id: 1,
      ExpenseAccount: null,
      Description: "",
      DueAmount: "",
      PayAmount: "",
      BalAmount: "",
    },]);
    form2.resetFields();
    setShowBankField(false);
    setIsModalOpen(false);
  };

  const handlePaymentChange = (value) => {
    if (value === 1) {
      setShowBankField(true);
    } else {
      setShowBankField(false);
    }
  };

  const handleOk = async (values) => {
    // Check if data2 exists and contains at least one row
    if (!data2 || data2.length === 0) {
      message.error("At least one row is required.");
      return;
    }

    // Iterate through data2 and validate each row
    for (let i = 0; i < data2.length; i++) {
      const record = data2[i];

      if (!record.ExpenseAccount) {
        message.error(`Row ${i + 1}: Expense Account is required.`);
        return;
      }

      if (!record.Description?.trim()) {
        message.error(`Row ${i + 1}: Description is required.`);
        return;
      }

      if (record.DueAmount === "" || isNaN(record.DueAmount) || record.DueAmount < 0) {
        message.error(`Row ${i + 1}: Enter a valid Due Amount.`);
        return;
      }

      if (!record.PayAmount || isNaN(record.PayAmount) || Number(record.PayAmount) <= 0) {
        message.error(`Row ${i + 1}: Enter a valid Pay Amount.`);
        return;
      }

      if (record.BalAmount === "" || isNaN(record.BalAmount) || record.BalAmount < 0) {
        message.error(`Row ${i + 1}: Enter a valid Balance Amount.`);
        return;
      }
    }


    setLoading(true);
    const expenseVoucherArray = data2.map((item) => item.ExpenseAccount);
    const descriptionArray = data2.map((item) => item.Description);
    const dueArray = data2.map((item) => item.DueAmount);
    const amountArray = data2.map((item) => item.PayAmount);
    const balanceArray = data2.map((item) => item.BalAmount);

    const data = {
      ...values,
      description: values.description ? values.description : "",
      bank_account: values.bank_account ? values.bank_account : "",
      payment_date: values.payment_date.format("YYYY-MM-DD"),
      voucher_date: values.voucher_date.format("YYYY-MM-DD"),
      expense_voucher: expenseVoucherArray,
      ev_description: descriptionArray,
      due_amount: dueArray,
      amount: amountArray,
      balance_amount: balanceArray,
      attachment: values.attachment ? values.attachment : null,
      totalVoucherAmount: totalPayingAmount,
    };
    const response = await expenseVoucherService.makePayment(data);
    if (response?.success) {
      notification.success({
        message: "Successfully Added!",
        description: response.message,
        placement: "topRight",
      });
    }
    setLoading(false);
    handleCancel();
  };

  return (
    <Modal
      title={<div style={{ width: "100%" }}>Make a Payment</div>}
      open={isVisible}
      onCancel={() => handleCancel(true)}
      footer={null}
      closable={true}
      width={700}
      style={{ padding: "15px" }}
    >
      <Form
        layout="vertical"
        form={form2}
        onFinish={handleOk}
        requiredMark={false}
        initialValues={{
          voucher_date: dayjs().tz(),
        }}
      >
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item
              name="payment_mode"
              rules={[
                {
                  required: true,
                  message: "Please select an appointment type",
                },
              ]}
            >
              <MaterialLikeSelect
                label="Payment Mode"
                required
                value={form2.getFieldValue("payment_mode")}
                onChange={(value) => {
                  handlePaymentChange(value)
                  form2.setFieldValue('payment_mode', value)
                }}
                options={paymentmodes?.map((payment) => ({
                  id: payment.id,
                  value: payment.id,
                  label: payment.name
                }))}
              />
              {/* <Select
                placeholder="Select Payment Mode"
                onChange={(value) => handlePaymentChange(value)}
              >
                {paymentmodes.map((payment) => (
                  <Select.Option key={payment.id} value={payment.id}>
                    {payment.name}
                  </Select.Option>
                ))}
              </Select> */}
            </Form.Item>
          </Col>

          {showBankField && (
            <Col span={8}>
              <Form.Item
                name="bank_account"
                rules={[
                  {
                    required: true,
                    message: "Please select a bank",
                  },
                ]}
              >
                <MaterialLikeSelect
                  label="Select Bank"
                  required
                  value={form2.getFieldValue("bank_account")}
                  onChange={(value) => form2.setFieldValue('bank_account', value)}
                  options={banks?.map((bank) => ({
                    id: bank.id,
                    value: bank.id,
                    label: bank.name
                  }))}
                />
                {/* <Select placeholder="Select Bank">
                  {banks.map((bank) => (
                    <Select.Option key={bank.id} value={bank.id}>
                      {bank.name}
                    </Select.Option>
                  ))}
                </Select> */}
              </Form.Item>
            </Col>
          )}

          <Col span={8}>
            <Form.Item
              name="company"
              rules={[
                {
                  required: true,
                  message: "Please select an appointment type",
                },
              ]}
            >
              <MaterialLikeSelect
                label="Inter-Company"
                required
                value={form2.getFieldValue("company")}
                onChange={(value) => form2.setFieldValue('company', value)}
                options={companies?.map((company) => ({
                  id: company.id,
                  value: company.id,
                  label: company.name
                }))}
              />
              {/* <Select placeholder="Select Inter-Company">
                {companies?.map((company) => (
                  <Select.Option key={company.id} value={company.id}>
                    {company.name}
                  </Select.Option>
                ))}
              </Select> */}
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="voucher_number"
              rules={[{ required: true, message: "Please enter the end time" }]}
            >
              <MaterialLikeInput
                label="Payment Voucher Number"
                required
                value={form2.getFieldValue("voucher_number")}
                onChange={(e) => form2.setFieldValue("voucher_number", e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="voucher_date"
              rules={[{ required: true, message: "Please enter the end time" }]}
            >
              <MaterialLikeDatePicker
                label="Voucher Date"
                required
                value={form2.getFieldValue("voucher_date")}
                onChange={(date) => form2.setFieldValue("voucher_date", date)}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="vendor"
              rules={[
                {
                  required: true,
                  message: "Please select an appointment type",
                },
              ]}
            >
              <MaterialLikeSelect
                label="Vendor Name"
                required
                value={form2.getFieldValue("vendor")}
                onChange={(value) => form2.setFieldValue('vendor', value)}
                options={vendors?.map((vendor) => ({
                  id: vendor.id,
                  value: vendor.id,
                  label: vendor.name
                }))}
              />
              {/* <Select placeholder="Select type">
                {vendors?.map((vendor) => (
                  <Select.Option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </Select.Option>
                ))}
              </Select> */}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="payment_date"
              rules={[{ required: true, message: "Please enter the end time" }]}
            >
              <MaterialLikeDatePicker
                label="Actual Payment Date"
                required
                value={form2.getFieldValue("payment_date")}
                onChange={(date) => form2.setFieldValue("payment_date", date)}
              />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item name="description">
              <MaterialLikeTextArea
                label="Special Note"
                rows={5}
                value={form2.getFieldValue("description")}
                onChange={(e) => form2.setFieldValue("description", e.target.value)}
              />
              {/* <Input.TextArea
                placeholder="Enter special notes here..."
                rows={5}
              /> */}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item  name="attachment">
              <Upload
                id="uploadInput"
                action="/upload"
                beforeUpload={() => false}
                listType="picture"
                style={{
                  width: "100%",
                }}
              >
                <Button
                  type="dashed"
                  icon={<UploadOutlined />}
                  style={{
                    width: "100%",
                    border: "1px solid #ccc",
                    padding: 18,
                  }}
                >
                  No File Chosen
                </Button>
              </Upload>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Table
              columns={columns2}
              scroll={{ x: "max-content" }}
              dataSource={data2}
              pagination={false}
              rowKey="Id"
              style={{ marginTop: "10px" }}
            />
            <div className="flex justify-between mt-5">
              <p>Total Paying Amount</p>
              <p>${totalPayingAmount}</p>
            </div>
            <Divider />
            <div
              style={{ marginTop: 20 }}
              className="flex flex-col justify-center items-center"
            >
              <CiSquarePlus
                style={{ cursor: "pointer", fontSize: "25px" }}
                onClick={handleAddRow}
              />
            </div>
          </Col>
        </Row>
        <Form.Item style={{ textAlign: "right", marginTop: 20 }}>
          <Button
            type="primary"
            htmlType="submit"
            style={{ width: "20%" }}
            loading={loading}
          >
            Make a Payment
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default PayablePayment;
